import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useHistory } from "react-router-dom";
import CustomButton from "../../../components/Common/CustomButton";
import { saveObject, toastError, toastSuccess } from "../../../utils";
import registerValidation from "../../../validations/registerValidation";
import { setOTP, verifyOTP } from "../actions";
import AuthInput from "../AuthInput";

const intialRequestObj = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  phoneNumber: "",
};

const PhoneIconIcon = () => (
  <svg className="w-4 h-4 text-[#8a9fb4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

export default function SignUp({ setView, initialEmail, setEmail, initialPassword, setPassword }) {
  const history = useHistory();
  const [user, setUser] = useState({
    ...intialRequestObj,
    email: initialEmail || "",
    password: initialPassword || "",
    confirmPassword: initialPassword || "",
  });
  const [country, setCountry] = useState("us");
  const [dialCode, setDialCode] = useState("1");
  const [phoneVal, setPhoneVal] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState("");
  const [isCooldown, setIsCooldown] = useState(false);
  const [timer, setTimer] = useState(60);

  const onChangeHandler = (e) => {
    const { name = "", value = "" } = e.target;
    const newUser = { ...user, [name]: value };
    newUser.username = newUser.firstName + newUser.lastName;
    setUser(newUser);
    setErrors({ ...errors, [name]: "" });

    // Sync state back to parent
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
  };

  const handleCountryChange = (val, countryData) => {
    if (countryData && countryData.dialCode) {
      setCountry(countryData.countryCode);
      setDialCode(countryData.dialCode);

      const digits = phoneVal.replace(/\D/g, "");
      const fullPhone = "+" + countryData.dialCode + digits;
      setUser((prev) => ({ ...prev, phoneNumber: fullPhone }));
    }
    setErrors({ ...errors, phoneNumber: "" });
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    const cleaned = val.replace(/[^\d\s\-\(\)]/g, "");
    setPhoneVal(cleaned);

    const digits = cleaned.replace(/\D/g, "");
    const fullPhone = dialCode ? "+" + dialCode + digits : digits;
    setUser((prev) => ({ ...prev, phoneNumber: fullPhone }));
    setErrors({ ...errors, phoneNumber: "" });
  };

  const onSubmitUserDetails = async () => {
    try {
      setIsLoading(true);
      const digits = phoneVal.replace(/\D/g, "");
      const fullPhone = digits ? "+" + dialCode + digits : "";

      const finalUser = {
        ...user,
        phoneNumber: fullPhone,
      };

      const { isValid, errors } = registerValidation(finalUser);
      if (!isValid) return setErrors(errors);

      const { email, phoneNumber, firstName } = finalUser;

      const res = await setOTP({ email, phoneNumber, firstName });
      if (res?.success) {
        toastSuccess("OTP has been sent to your registered email");
        setShowOTP(true);
        setIsCooldown(true);
      }
    } catch (err) {
      toastError(err?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onRegisterUser = async (otpVal) => {
    try {
      setIsLoading(true);
      const finalOtp = typeof otpVal === "string" ? otpVal : otp;

      if (finalOtp.trim().length === 6) {
        const digits = phoneVal.replace(/\D/g, "");
        const fullPhone = digits ? "+" + dialCode + digits : "";
        const finalUser = {
          ...user,
          phoneNumber: fullPhone,
        };

        const res = await verifyOTP({ ...finalUser, otp: finalOtp });

        if (res?.success) {
          toastSuccess("User registration successful");
          saveObject("auth", res.data);
          history.push("/welcome");
          setUser({ ...intialRequestObj });
          setPhoneVal("");
          setOtp("");
          setShowOTP(false);
        }
      } else {
        toastError("Enter 6 digit OTP first");
      }
    } catch (err) {
      toastError(err?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (val) => {
    setOtp(val);
    if (val.trim().length === 6 && !isLoading) {
      setTimeout(() => {
        onRegisterUser(val);
      }, 50);
    }
  };

  const handleResendOTP = async () => {
    try {
      if (isCooldown) return;
      setIsCooldown(true);
      const digits = phoneVal.replace(/\D/g, "");
      const fullPhone = digits ? "+" + dialCode + digits : "";
      const finalUser = {
        ...user,
        phoneNumber: fullPhone,
      };
      const { email, phoneNumber, firstName } = finalUser;

      const res = await setOTP({ email, phoneNumber, firstName });
      if (res?.success) {
        toastSuccess("OTP has been Resent to your registered email");
        setShowOTP(true);
      }
    } catch (err) {
      toastError(err?.data?.message);
    } finally {
      setIsCooldown(true);
      setTimer(60);
    }
  };

  useEffect(() => {
    let interval;
    if (isCooldown) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(interval);
            setIsCooldown(false);
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isCooldown]);

  return (
    <div className="flex flex-col gap-4">
      <AnimatePresence mode="wait">
        {showOTP ? (
          <motion.div
            key="otp-form"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="flex flex-col gap-4 text-center"
          >
            <div className="flex flex-col gap-1.5">
              <h2 className="text-2xl font-bold text-[#0a2540] tracking-tight">
                Verify Email Address
              </h2>
              <p className="text-xs text-slate-500">
                Please enter the 6-digit verification code sent to
              </p>
              <b className="text-[#0a2540] text-xs font-semibold">{user.email}</b>
            </div>

            {/* Modern Spaced-out OTP Inputs */}
            <div className="flex justify-center my-1">
              <OtpInput
                value={otp}
                onChange={handleOtpChange}
                numInputs={6}
                containerStyle={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                }}
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{ ...props.style, width: "38px", height: "44px" }}
                    className="bg-[#f0f4fa] border border-[#e1e9f4] focus:border-[#313C4C] focus:ring-2 focus:ring-[#313C4C]/20 rounded-xl text-center text-[#313C4C] font-bold text-lg shadow-sm outline-none transition-all duration-200"
                  />
                )}
              />
            </div>

            <div className="flex justify-center gap-2 items-center">
              <span className="text-xs text-slate-500">Didn't receive the OTP?</span>
              <button
                className="text-xs font-semibold text-[#313C4C] hover:text-sky-800 disabled:opacity-50 disabled:cursor-not-allowed underline transition-all bg-transparent border-none cursor-pointer"
                onClick={handleResendOTP}
                disabled={isCooldown}
              >
                {isCooldown ? `Resend in ${timer}s` : "Resend OTP"}
              </button>
            </div>

            <p className="text-xs text-slate-500 text-center -mt-2">
              Can't find the email? Please check your spam or junk folder too.
            </p>

            <div className="mt-1">
              <CustomButton
                onClick={() => onRegisterUser()}
                loader={isLoading}
                disabled={isLoading}
                className="bg-[#0a2540] hover:bg-[#001c3d] text-white py-3.5 rounded-2xl font-semibold tracking-wide shadow-lg shadow-[#0a2540]/10 hover:shadow-[#0a2540]/20 transition-all duration-300 w-full text-sm border-none cursor-pointer"
              >
                Verify
              </CustomButton>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="signup-form"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="flex flex-col gap-3.5"
          >
            {/* Header Text */}
            <div className="text-center flex flex-col gap-1 mb-1">
              <h2 className="text-2xl font-bold text-[#0a2540] tracking-tight">
                Create Account
              </h2>
              <p className="text-xs font-medium text-slate-400 max-w-[320px] mx-auto leading-relaxed">
                Join the global logistics network.
              </p>
            </div>

            {/* Form Input fields */}
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <AuthInput
                  name="firstName"
                  placeholder="John"
                  label="First Name"
                  value={user.firstName}
                  onChange={onChangeHandler}
                  error={!!errors.firstName}
                  errorMsg={errors.firstName}
                />
                <AuthInput
                  name="lastName"
                  placeholder="Doe"
                  label="Last Name"
                  value={user.lastName}
                  onChange={onChangeHandler}
                  error={!!errors.lastName}
                  errorMsg={errors.lastName}
                />
              </div>
              <AuthInput
                name="email"
                placeholder="john.doe@company.com"
                label="Work Email"
                value={user.email}
                onChange={onChangeHandler}
                error={!!errors.email}
                errorMsg={errors.email}
              />

              {/* Split Phone Input: Country Code Selector + Phone Number */}
              <div className="flex flex-col w-full gap-1">
                <label className="text-[12.5px] font-semibold text-slate-600">Phone Number</label>
                <div className="flex gap-2.5 items-center w-full">
                  <div className="relative flex-shrink-0 w-[110px]">
                    <PhoneInput
                      country={country}
                      value={dialCode}
                      onChange={handleCountryChange}
                      enableSearch
                      disableDropdown={false}
                      specialLabel=""
                      inputStyle={{
                        width: "110px",
                        height: "46px",
                        borderRadius: "16px",
                        border: errors.phoneNumber ? "1px solid #f87171" : "1px solid #e1e9f4",
                        backgroundColor: "#f0f4fa",
                        paddingLeft: "58px",
                        fontSize: "13.5px",
                        color: "#1e293b",
                        fontWeight: "600",
                        pointerEvents: "none",
                      }}
                      buttonStyle={{
                        width: "40px",
                        height: "46px",
                        borderRadius: "16px 0 0 16px",
                        border: "none",
                        backgroundColor: "transparent",
                        paddingLeft: "2px",
                      }}
                      dropdownStyle={{
                        zIndex: 1000,
                        width: "280px",
                      }}
                    />
                  </div>
                  <div className="relative flex-grow">
                    <div
                      className={`relative flex items-center w-full bg-[#f0f4fa] rounded-2xl border transition-all duration-200 h-[46px] overflow-hidden ${errors.phoneNumber
                        ? "border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
                        : "border-[#e1e9f4] focus-within:border-[#313C4C] focus-within:ring-2 focus-within:ring-[#313C4C]/10"
                        }`}
                    >
                      <div className="pl-4 pr-1 flex items-center justify-center">
                        <PhoneIconIcon />
                      </div>
                      <input
                        type="text"
                        name="phoneVal"
                        value={phoneVal}
                        placeholder="555-000-0000"
                        onChange={handlePhoneChange}
                        className="w-full bg-transparent border-none outline-none text-slate-800 placeholder-slate-400/80 text-[13.5px] h-full pl-2 pr-4"
                      />
                    </div>
                  </div>
                </div>
                {errors.phoneNumber && (
                  <span className="text-xs text-red-500 mt-0.5 ml-1 leading-none">{errors.phoneNumber}</span>
                )}
              </div>

              <AuthInput
                type="password"
                name="password"
                placeholder="••••••••"
                label="Password"
                value={user.password}
                onChange={onChangeHandler}
                error={!!errors.password}
                errorMsg={errors.password}
              />
              <AuthInput
                type="password"
                name="confirmPassword"
                placeholder="••••••••"
                label="Confirm Password"
                value={user.confirmPassword}
                onChange={onChangeHandler}
                error={!!errors.confirmPassword}
                errorMsg={errors.confirmPassword}
              />

              {/* Visual Checkbox */}
              <div className="flex items-center gap-2.5 my-0.5">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  className="w-4 h-4 text-[#0a2540] border-[#e1e9f4] rounded focus:ring-[#0a2540]/10 accent-[#0a2540] cursor-pointer"
                  defaultChecked
                />
                <label htmlFor="agreeTerms" className="text-xs text-slate-500 font-medium select-none cursor-pointer leading-tight">
                  I agree to the <span className="font-semibold text-slate-700 hover:underline">Terms</span> and <span className="font-semibold text-slate-700 hover:underline">Privacy Policy</span>.
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-1">
              <CustomButton
                onClick={onSubmitUserDetails}
                loader={isLoading}
                disabled={isLoading}
                className="bg-[#0a2540] hover:bg-[#001c3d] text-white py-3.5 rounded-2xl font-semibold tracking-wide shadow-lg shadow-[#0a2540]/10 hover:shadow-[#0a2540]/20 transition-all duration-300 w-full text-sm border-none cursor-pointer"
              >
                Create Account
              </CustomButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer Link */}
      <div className="text-center pt-2">
        <p className="text-sm text-slate-500">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => setView("login")}
            className="text-[#313C4C] font-bold hover:text-sky-800 transition-colors duration-200 focus:outline-none cursor-pointer bg-transparent border-none"
          >
            Login here
          </button>
        </p>
      </div>
    </div>
  );
}
